<template>
	<NavSidebar
		v-bind="bindings"
		@nav="onNav"
		@action="onAction"
	/>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import NavSidebar from "./NavSidebar";
import { Themes as GroupThemes, Scales as GroupScales } from "@/components/Sidebar/NavSidebarGroup";
import AppConfig from "@/constants/AppConfig";

export default {
	name: "NavSidebarHOC",
	components: {
		NavSidebar,
	},
	computed: {
		...mapGetters( "auth", [ "timezone", ] ),
		...mapGetters( "userProfile", [ "userProfile", ] ),
		...mapGetters( [
			"myTeams",
			"myFavoriteTeams",
			"myFavoriteOneOnOnes",
		] ),
		...mapGetters( "sidebar", [ "toggleState", ] ),
		primaryNavGroups() {
			const { name: routeName, path: routePath, } = this.$route;

			/**
			 * Menu group schema
			 * @prop {String} key
			 * @prop {String} text
			 * @prop {String} tooltip
			 * @prop {Array|null} icon
			 * @prop {Boolean} avatar
			 * @prop {Boolean} isSelected
			 * @prop {Object} emits
			 * @prop {String} emits.eventName - "nav" | "action"
			 * @prop {Object} emits.eventArgs - { name, [params, meta] } | { name, [ value ] }
			 * @prop {Array} [primaryItems]
			 * @prop {Array} [secondaryItems]
			 * @prop {String} [theme]
			 * @prop {String} [scale]
			 */

			const { myFavoriteTeams, userProfile = {}, } = this;
			const { isLegacyMeetingUser = false, } = userProfile;
			const MAX_TOP_TEAMS = 5;

			const oneOnOnesGroup = {
				key: "nav:one-on-ones",
				text: this.$t( "sidebar.NavSidebarHOC_oneOnOnesGroupTitle" ),
				tooltip: this.$t( "sidebar.NavSidebarHOC_oneOnOnesGroupTitle" ),
				icon: [ "fac", "one-on-one", ],
				avatar: false,
				isSelected: routePath.startsWith( "/one-on-one" ),
				emits: {
					eventName: "action",
					eventArgs: { name: "sidebar/navigateToOneOnOnesPage", },
				},
			};

			oneOnOnesGroup.primaryItems = this.myFavoriteOneOnOnes.slice( 0, MAX_TOP_TEAMS ).map( oneOnOne => {
				const { id: teamId, } = oneOnOne;
				const teamPath = `/t2/one-on-one/${ teamId }`;
				return {
					key: `nav:1on1-${ teamId }`,
					text: oneOnOne.name,
					isSelected: routePath.startsWith( teamPath ),
					emits: {
						eventName: "action",
						eventArgs: { name: "sidebar/navigateToOneOnOnePage", value: { teamId, }, },
					},
				};
			} );

			oneOnOnesGroup.secondaryItems = this.myFavoriteOneOnOnes.slice( MAX_TOP_TEAMS ).map( oneOnOne => {
				const { id: teamId, } = oneOnOne;
				const teamPath = `/t2/one-on-one/${ teamId }`;
				return {
					key: `nav:1on1-${ teamId }`,
					text: oneOnOne.name,
					isSelected: routePath.startsWith( teamPath ),
					emits: {
						eventName: "action",
						eventArgs: { name: "sidebar/navigateToOneOnOnePage", value: { teamId, }, },
					},
				};
			} );

			const teamsGroup = {
				key: "nav:teams",
				text: this.$t( "sidebar.NavSidebarHOC_teamsGroupTitle" ),
				tooltip: this.$t( "sidebar.NavSidebarHOC_teamsGroupTitle" ),
				icon: [ "fas", "users", ],
				avatar: false,
				isSelected: routeName === "teams",
				emits: {
					eventName: "action",
					eventArgs: { name: "sidebar/navigateToTeamsPage", value: {}, },
				},
			};

			teamsGroup.primaryItems = myFavoriteTeams.slice( 0, MAX_TOP_TEAMS ).map( team => {
				const { id: teamId, } = team;
				const teamPath = `/t2/team/${ teamId }`;
				return {
					key: `nav:team-${ team.id }`,
					text: team.name,
					isSelected: routePath.startsWith( teamPath ),
					emits: {
						eventName: "action",
						eventArgs: { name: "sidebar/navigateToTeamPage", value: { teamId, }, },
					},
				};
			} );

			teamsGroup.secondaryItems = myFavoriteTeams.slice( MAX_TOP_TEAMS ).map( team => {
				const { id: teamId, } = team;
				const teamPath = `/t2/team/${ teamId }`;
				return {
					key: `nav:team-${ team.id }`,
					text: team.name,
					isSelected: routePath.startsWith( teamPath ),
					emits: {
						eventName: "action",
						eventArgs: { name: "sidebar/navigateToTeamPage", value: { teamId, }, },
					},
				};
			} );

			const profileGroup = {
				key: "nav:profile",
				text: this.$t( "sidebar.NavSidebarHOC_profileGroupTitle" ),
				tooltip: this.$t( "sidebar.NavSidebarHOC_profileGroupTitle" ),
				icon: null,
				avatar: true,
				isSelected: routeName === "profile",
				emits: {
					eventName: "nav",
					eventArgs: { name: "profile", },
				},
			};

			const meetingsGroup = {
				key: "nav:meetings",
				text: this.$t( "sidebar.NavSidebarHOC_meetingsGroupTitle" ),
				tooltip: this.$t( "sidebar.NavSidebarHOC_meetingsGroupTitle" ),
				icon: [ "fas", "calendar-star", ],
				avatar: false,
				// schedule
				// schedule-meeting
				isSelected: routeName.startsWith( "schedule" ),
				emits: {
					eventName: "nav",
					eventArgs: { name: "schedule", },
				},
			};

			const goalsGroup = {
				key: "nav:goals",
				text: this.$t( "sidebar.NavSidebarHOC_goalsGroupTitle" ),
				tooltip: this.$t( "sidebar.NavSidebarHOC_goalsGroupTitle" ),
				icon: [ "fas", "bullseye-arrow", ],
				avatar: false,
				isSelected: routeName === "goals",
				emits: {
					eventName: "nav",
					eventArgs: { name: "goals", },
				},
			};

			const commitmentsGroup = {
				key: "nav:commitments",
				text: this.$t( "sidebar.NavSidebarHOC_commitmentsGroupTitle" ),
				tooltip: this.$t( "sidebar.NavSidebarHOC_commitmentsGroupTitle" ),
				icon: [ "fas", "check-double", ],
				avatar: false,
				isSelected: routeName === "commitments",
				emits: {
					eventName: "action",
					eventArgs: { name: "sidebar/navigateToMyActionItemsPage", value: {}, },
				},
			};

			return [
				oneOnOnesGroup,
				teamsGroup,
				profileGroup,
				isLegacyMeetingUser ? meetingsGroup : null,
				goalsGroup,
				commitmentsGroup,
			].filter( itemGroup => !!itemGroup )
				.map( itemGroup => {
					// set some defaults
					return {
						primaryItems: [],
						secondaryItems: [],
						theme: GroupThemes.PRIMARY,
						scale: GroupScales.NORMAL,
						...itemGroup,
					};
				} );
		},
		secondaryNavGroups() {
			const { userProfile = {}, } = this;
			const { showPaymentOption = false, } = userProfile;

			const subscribeGroup = {
				key: "nav:subscribe",
				text: this.$t( "sidebar.NavSidebarHOC_subscribeGroupTitle" ),
				tooltip: this.$t( "sidebar.NavSidebarHOC_subscribeGroupTitle" ),
				icon: [ "fas", "dollar-sign", ],
				avatar: false,
				isSelected: false,
				emits: {
					eventName: "nav",
					eventArgs: {
						name: "stripe",
						personId: this.userProfile.personId,
						email: this.userProfile.email,
					},
				},
				theme: GroupThemes.PAYMENT,
			};

			let coachingDemoGroup;
			if ( showPaymentOption ) {
				coachingDemoGroup = {
					key: "nav:coaching",
					text: this.$t( "sidebar.NavSidebarHOC_demoGroupTitle" ),
					tooltip: this.$t( "sidebar.NavSidebarHOC_demoGroupTitle" ),
					icon: [ "fas", "comments-question-check", ],
					avatar: false,
					isSelected: false,
					emits: {
						eventName: "nav",
						eventArgs: { name: "demo", },
					},
					theme: GroupThemes.SPECIAL,
				};
			} else {
				coachingDemoGroup = {
					key: "nav:coaching",
					text: this.$t( "sidebar.NavSidebarHOC_coachingGroupTitle" ),
					tooltip: this.$t( "sidebar.NavSidebarHOC_coachingGroupTitle" ),
					icon: [ "fas", "comments-question-check", ],
					avatar: false,
					isSelected: false,
					emits: {
						eventName: "nav",
						eventArgs: { name: "coaching", },
					},
					theme: GroupThemes.SPECIAL,
				};
			}

			const settingsGroup = {
				key: "nav:settings",
				text: this.$t( "sidebar.NavSidebarHOC_settingsGroupTitle" ),
				tooltip: this.$t( "sidebar.NavSidebarHOC_settingsGroupTitle" ),
				icon: [ "fas", "sliders-h", ],
				avatar: false,
				isSelected: false,
				emits: {
					eventName: "nav",
					eventArgs: { name: "settings", },
				},
			};

			const signOutGroup = {
				key: "nav:sign-out",
				text: this.$t( "sidebar.NavSidebarHOC_signOutGroupTitle" ),
				tooltip: this.$t( "sidebar.NavSidebarHOC_signOutGroupTitle" ),
				icon: [ "fas", "power-off", ],
				avatar: false,
				isSelected: false,
				emits: {
					eventName: "action",
					eventArgs: { name: "auth/logoutUser", },
				},
			};

			return [
				AppConfig.showStripeIntegration && this.userProfile.showPaymentOption ? subscribeGroup : null,
				coachingDemoGroup,
				settingsGroup,
				signOutGroup,
			].filter( itemGroup => !!itemGroup )
				.map( itemGroup => {
					// set some defaults
					return {
						primaryItems: [],
						secondaryItems: [],
						theme: GroupThemes.PRIMARY,
						scale: GroupScales.SMALL,
						...itemGroup,
					};
				} );
		},
		bindings() {
			const {
				userProfile: user,
				primaryNavGroups,
				secondaryNavGroups,
				toggleState,
			} = this;

			return {
				user,
				primaryNavGroups,
				secondaryNavGroups,
				toggleState,
			};
		},
	},
	methods: {
		...mapActions( "meeting", [ "leaveMeeting", ] ),
		...mapActions( "sidebar", [ "closeNavSidebar", ] ),
		async onNav( eventArgs ) {
			if ( eventArgs.name === "coaching" ) {
				return window.open( AppConfig.coachingLink, "_blank" );
			}

			if ( eventArgs.name === "demo" ) {
				return window.open( AppConfig.demoLink, "_blank" );
			}

			if ( eventArgs.name === "stripe" ) {
				return window.location.assign( `${ AppConfig.stripePaymentLink }?client_reference_id=${ this.userProfile.personId }&prefilled_email=${ this.userProfile.email }` );
			}

			if ( eventArgs.name === "settings" ) {
				return window.location.assign( "/t2/me/settings" );
			}

			if ( eventArgs.name === "profile" ) {
				return window.location.assign( "/t2/me/profile" );
			}

			if ( eventArgs.name !== "schedule" ) {
				this.leaveMeeting();
			}

			this.closeNavSidebar();
			this.$router.push( eventArgs );
		},
		onAction( eventArgs ) {
			this.closeNavSidebar();
			this.$store.dispatch( eventArgs.name, eventArgs.value );
		},
	},
};
</script>



// WEBPACK FOOTER //
// src/components/Sidebar/NavSidebarHOC.vue